import queryString from 'querystring';
import Api from '../api';
import type { ItemColorAndSize } from '../Hooks/ItemService/types';
import { UpdateKeyDetailsInputs } from '../../Components/SellToUs/schemas/updateKeyDetails';
import { VendorCatalogItem as VendorCatalogItemOutput } from '../Hooks/ItemService/types';

const ITEM_SERVICE = '/items';
const VENDOR_CATALOG = '/vendor-catalog';

export type DraftPurchaseOrder = {
  due_date?: Date | null;
  incoterm?: string | null;
  shipment_mode?: string | null;
  country_of_origin?: string | null;
};

export type VendorCatalogItem = {
  id: string;
  style_number: string | null;
  vendor_name: string;
  vendor_internal_id: string;
  photos: Array<{
    type: 'front' | 'swatch' | 'fit';
    s3_key: string;
    content_type: 'image/jpeg' | 'image/png' | 'image/jpg';
    is_netsuite_image: boolean;
  }>;
  category: string | null;
  department?: string | null;
  class: string | null;
  jr_cost: number | null;
  plus_cost: number | null;
  lead_time_in_weeks: number | null;
  created_at?: Date;
  updated_at?: Date;
  jr_vendor_ref?: string | null;
  plus_vendor_ref?: string | null;
  division?: string | null;
  fashionnova_exclusive?: boolean;
  reorder_lead_time_in_weeks?: number | null;
  stock_quantity?: number | null;
  stock_ratio?: string | null;
  stock_ihd?: Date | null;
  incoming_stock_quantity?: number | null;
  incoming_stock_ratio?: string | null;
  incoming_stock_date?: Date | null;
  length?: number | null;
  inseam?: number | null;
  stretch?: string | null;
  neckline?: string | null;
  sleeve_length?: string | null;
  top_length?: string | null;
  body_fit_top?: string | null;
  body_fit_bottom?: string | null;
  bottom_length?: string | null;
  fabric_content?: string | null;
  fabric_weight?: number | null;
  fabric_code_and_name?: string | null;
  fabric?: string | null;
  pattern_print?: string | null;
  wash?: string | null;
  heel_height?: string | null;
  heel_type?: string | null;
  shoe_style?: string | null;
  vendor_notes?: string | null;
  draft_purchase_order?: Partial<DraftPurchaseOrder>;
  color_and_sizes?: ItemColorAndSize[];
  fit_sample?: boolean;
  buyer_pick?: boolean;
};

export type VendorSendDetails = {
  item_fields: Required<
    Pick<
      VendorCatalogItem,
      | 'style_number'
      | 'category'
      | 'department'
      | 'class'
      | 'jr_vendor_ref'
      | 'plus_vendor_ref'
      | 'lead_time_in_weeks'
      | 'division'
      | 'fashionnova_exclusive'
      | 'reorder_lead_time_in_weeks'
      | 'stock_quantity'
      | 'stock_ratio'
      | 'stock_ihd'
      | 'incoming_stock_quantity'
      | 'incoming_stock_ratio'
      | 'incoming_stock_date'
      | 'length'
      | 'inseam'
      | 'stretch'
      | 'neckline'
      | 'sleeve_length'
      | 'top_length'
      | 'body_fit_top'
      | 'body_fit_bottom'
      | 'bottom_length'
      | 'fabric_weight'
      | 'fabric_code_and_name'
      | 'fabric'
      | 'pattern_print'
      | 'wash'
      | 'heel_height'
      | 'heel_type'
      | 'shoe_style'
    >
  >;
  draft_po_fields: Required<DraftPurchaseOrder>;
};

export type UpdateItem = Partial<
  Omit<VendorCatalogItem, 'id' | 'created_at' | 'updated_at'>
>;

export type ItemToUpload = Required<
  Pick<
    VendorCatalogItem,
    | 'style_number'
    | 'category'
    | 'class'
    | 'jr_cost'
    | 'plus_cost'
    | 'lead_time_in_weeks'
    | 'division'
    | 'department'
    | 'fit_sample'
    | 'fashionnova_exclusive'
    | 'buyer_pick'
  > & {
    country_of_origin: string;
    photo_content_types: {
      frontImageContentType: string;
      swatchImageContentType: string;
    };
  }
>;

export type ItemToUploadOutput = Partial<VendorCatalogItem> & {
  front_photo_upload_data: { url: string; content_type: string };
  swatch_photo_upload_data?: { url: string; content_type: string };
};

export type BulkCreateItems = {
  items: ItemToUpload[];
};

export type PhotosContentType = {
  frontImageContentType: string;
  backImageContentType: string;
  swatchImageContentType: string;
};

export type PaginatedItemsResponse<T = VendorCatalogItem> = {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount: number;
};

export type GetVendorItemsParams = {
  filter: string;
  cursor?: string;
  limit?: number;
};

const ItemServiceApi = {
  async getVendorItems<T = VendorCatalogItem>(
    userId: string,
    params: GetVendorItemsParams,
  ): Promise<PaginatedItemsResponse<T>> {
    return Api.get(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}?${queryString.encode(
        params,
      )}`,
    );
  },

  async getVendorItem(userId: string, itemId: string) {
    return Api.get(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/${itemId}`,
    );
  },

  async getItemPhotoURLs(userId: string, itemId: string) {
    return Api.get(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/${itemId}/photos`,
    );
  },

  async countVendorCatalogItems(userId: string) {
    return Api.get(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/countByStage`,
    );
  },

  async createVendorItems(
    userId: string,
    data: ItemToUpload | BulkCreateItems,
  ): Promise<{ items: ItemToUploadOutput[] } | ItemToUploadOutput> {
    return Api.post(`/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}`, {
      data,
    });
  },

  async updateVendorItem(userId: string, itemId: string, data: UpdateItem) {
    return Api.patch(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/${itemId}`,
      { data },
    );
  },

  async submitItemDetails(
    userId: string,
    itemId: string,
    data: VendorSendDetails,
  ) {
    return Api.post(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/${itemId}/submit-details`,
      { data },
    );
  },

  async getUploadUrlForItemPhotos(userId: string, data: PhotosContentType) {
    return Api.put(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/photos/upload-url`,
      {
        data,
      },
    );
  },

  async getSignedCookiesForItemPhotos(userId: string) {
    return Api.get(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/photos/signed-cookies`,
    );
  },

  async countRemainingItem(userId: string) {
    return Api.get(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/count-remaining-item`,
    );
  },

  async updateItemKeyDetails(
    userId: string,
    itemId: string,
    data: UpdateKeyDetailsInputs,
  ): Promise<{
    front_photo_upload_url?: string;
    swatch_photo_upload_url?: string;
    front_photo_url?: string;
    swatch_photo_url?: string;
    item: VendorCatalogItemOutput;
  }> {
    return Api.patch(
      `/users/${userId}${ITEM_SERVICE}${VENDOR_CATALOG}/${itemId}/key-details`,
      { data },
    );
  },
};

export default ItemServiceApi;
